import { useEffect, useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";
import { v4 as uuidv4 } from "uuid"; // You might need to install this package
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { InventoryItem, MoverInventoryItem } from "@/types";
// import { useGetInventoryItemsQuery } from "@/store/mover/apis";
import { cn } from "@/lib/utils"; // Make sure you have this utility function
import InventoryToolbar from "@/pages/Moves/components/InventoryToolbar";
import InventoryTable, {
  CustomInventoryItem,
  InventoryItemForm,
} from "@/pages/Moves/components/InventoryTable";

type QuantityMap = {
  uuid: string;
  quantity: number;
}[];

const Inventory = ({
  trigger,
  inventoryItems,
  moverInventoryItems,
  onSave,
  showSummary = true,
  showAsModal = true,
  customerFacing = false,
  onSetInventory = () => {},
}: {
  trigger?: React.ReactNode;
  inventoryItems: InventoryItem[];
  moverInventoryItems: MoverInventoryItem[];
  onSave?: (items: InventoryItemForm[]) => void;
  showSummary?: boolean;
  showAsModal?: boolean;
  customerFacing?: boolean;
  onSetInventory?: (items: InventoryItemForm[]) => void;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [quantityMap, setQuantityMap] = useState<QuantityMap>([]);
  const [inventory, setInventory] = useState<InventoryItemForm[]>([]);
  const [customItems, setCustomItems] = useState<InventoryItemForm[]>([]);

  // Get the form context
  const formContext = useFormContext();

  // Check if the context exists
  if (!formContext) {
    // Option 1: Throw an error or return early if context is required
    // throw new Error("Inventory component must be used within a FormProvider");

    // Option 2: Render null or alternative content if the context is optional
    console.warn(
      "Inventory component rendered outside of FormProvider. Form functionality might be limited."
    );
    // return null; // Or some fallback UI
  }

  // If context exists, destructure the methods you need
  const { setValue, getValues } = formContext || {}; // Use default {} or handle potential null/undefined

  // Now you can safely use setValue and getValues,
  // potentially adding checks if they might be undefined based on the fallback above
  // Example: getValues?.("customer_name") instead of getValues("customer_name")

  const onSearchChange = (value: string) => {
    setSearchTerm(value);
    if (value) {
      setSelectedCategory(null);
    }
  };

  const onCategoryChange = (category: string | null) => {
    setSelectedCategory(category);
  };

  useEffect(() => {
    onSetInventory(
      inventory
        .filter((item) => item.quantity > 0)
        .map((item) => ({
          ...item,
        }))
    );
  }, [inventory, onSetInventory]);

  useEffect(() => {
    let tempInventory: InventoryItemForm[] = [];

    if (!moverInventoryItems || moverInventoryItems.length === 0) {
      return;
    }

    tempInventory = inventoryItems
      .filter((item: InventoryItem) => !item.mover_inventory_item_id)
      .sort((a: InventoryItem, b: InventoryItem) =>
        a.room_or_type.localeCompare(b.room_or_type || "")
      )
      .map((item: InventoryItem) => ({
        ...item,
        uuid: uuidv4(),
      }));

    tempInventory = [
      ...tempInventory,
      ...inventoryItems
        .filter((item: InventoryItem) => item.mover_inventory_item_id)
        .sort((a: InventoryItem, b: InventoryItem) =>
          a.room_or_type.localeCompare(b.room_or_type || "")
        )
        .map((item: InventoryItem) => ({
          ...item,
          uuid: uuidv4(),
        })),
    ];

    const alreadyPresentItems = inventoryItems.map(
      (item: InventoryItem) => item.mover_inventory_item_id
    );

    const restOfMoverInventoryItems = moverInventoryItems
      .filter(
        (item: MoverInventoryItem) => !alreadyPresentItems.includes(item.id)
      )
      .sort((a: MoverInventoryItem, b: MoverInventoryItem) => {
        const roomCompare = (a.room_or_type ?? "").localeCompare(
          b.room_or_type ?? ""
        );

        if (roomCompare !== 0) {
          // if rooms are different, return that result
          return roomCompare;
        } else {
          // compare by name
          return a.name.localeCompare(b.name);
        }
      });

    tempInventory = [
      ...tempInventory,
      ...restOfMoverInventoryItems.map((item: MoverInventoryItem) => ({
        ...item,
        id: null,
        uuid: uuidv4(),
        mover_inventory_item_id: item.id,
        quantity: 0,
        total_weight_lbs: 0,
        total_volume_cuft: 0,
      })),
    ];

    // set the initial inventory
    setInventory(tempInventory);

    // create a hash map of quantities
    const initialQuantities = tempInventory.map((item) => ({
      uuid: item.uuid,
      quantity: item.quantity || 0,
    }));
    setQuantityMap(initialQuantities);
  }, [inventoryItems, moverInventoryItems]);

  const filteredItems = useMemo(() => {
    return inventory.filter((item: InventoryItemForm) => {
      const matchesSearch = searchTerm
        ? item.name.toLowerCase().includes(searchTerm.toLowerCase())
        : true;

      const matchesCategory = selectedCategory
        ? item.room_or_type === selectedCategory
        : true;

      return searchTerm ? matchesSearch : matchesCategory;
    });
  }, [inventory, searchTerm, selectedCategory]);

  const addCustomItem = () => {
    const newCustomItem: InventoryItemForm = {
      id: null,
      mover_inventory_item_id: null,
      uuid: uuidv4(),
      name: "",
      description: null,
      room_or_type: "Custom",
      volume_cuft: 0,
      weight_lbs: 0,
      quantity: 0,
      total_weight_lbs: 0,
      total_volume_cuft: 0,
    };

    setCustomItems([newCustomItem, ...customItems]);
    setInventory([{ ...newCustomItem }, ...inventory]);
  };

  const updateCustomItem = (
    uuid: string,
    field: keyof CustomInventoryItem,
    value: string | number
  ) => {
    setInventory((prevInventory) =>
      prevInventory.map((item) =>
        item.uuid === uuid
          ? {
              ...item,
              [field]: value,
              total_weight_lbs:
                field === "weight_lbs"
                  ? (value as number) * item.quantity
                  : item.total_weight_lbs,
              total_volume_cuft:
                field === "volume_cuft"
                  ? (value as number) * item.quantity
                  : item.total_volume_cuft,
            }
          : item
      )
    );
  };

  const handleQuantityChange = (
    updatedItem: InventoryItemForm,
    quantity: number
  ) => {
    setQuantityMap((prev) => {
      const updatedMap = [...prev];
      const index = updatedMap.findIndex(
        (item) => item.uuid === updatedItem.uuid
      );

      if (index !== -1) {
        updatedMap[index].quantity = quantity;
      } else {
        updatedMap.push({
          uuid: updatedItem.uuid,
          quantity: quantity,
        });
      }

      return updatedMap;
    });

    setInventory((currentInventory) => {
      const foundItem = currentInventory.find(
        (item) => item.uuid === updatedItem.uuid
      );

      if (foundItem) {
        return currentInventory.map((item) => {
          if (item.uuid === updatedItem.uuid) {
            return {
              ...item,
              quantity: quantity,
              total_weight_lbs: quantity * (item.weight_lbs || 0),
              total_volume_cuft: quantity * (item.volume_cuft || 0),
            };
          }
          return item;
        });
      } else {
        return [
          ...currentInventory,
          {
            ...updatedItem,
            quantity: quantity,
            total_weight_lbs: quantity * (updatedItem.weight_lbs || 0),
            total_volume_cuft: quantity * (updatedItem.volume_cuft || 0),
          },
        ];
      }
    });
  };

  const getItemQuantity = (uuid: string) => {
    const found = quantityMap.find((item) => item.uuid === uuid);
    return found ? found.quantity : 0;
  };

  const deleteCustomItem = (uuid: string) => {
    setInventory(inventory.filter((item) => item.uuid !== uuid));
    setCustomItems(customItems.filter((item) => item.uuid !== uuid));
  };

  const saveValues = () => {
    if (!setValue) {
      console.error("Cannot save values: Form context not available.");
      setIsOpen(false); // Still close the sheet perhaps
      return;
    }
    const newInventory = inventory
      .filter((item) => item.quantity > 0)
      .map((item) => ({
        ...item,
      }));
    setValue("inventory_items", newInventory);
    onSave?.(newInventory);
    setIsOpen(false);
  };

  return (
    <div>
      {showAsModal ? (
        <Sheet open={isOpen} onOpenChange={setIsOpen}>
          <SheetTrigger asChild>
            {trigger || (
              <div className="text-sm cursor-pointer underline underline-offset-4 decoration-black">
                Add Inventory
              </div>
            )}
          </SheetTrigger>
          <SheetContent
            side="left"
            className={cn(
              "w-[80vw] max-w-[80vw]",
              "sm:max-w-[80vw]",
              "lg:max-w-[80vw]",
              "pb-32"
            )}
          >
            <SheetHeader>
              <SheetTitle className="mb-2">
                Move Inventory <span className="text-gray-500">for</span>{" "}
                {getValues ? getValues("customer_name") : "Customer"}
              </SheetTitle>
              <InventoryToolbar
                searchTerm={searchTerm}
                inventory={inventory}
                onSearchChange={onSearchChange}
                onSave={saveValues}
                showSummary={showSummary}
              />
            </SheetHeader>

            {/* {isLoadingMoverInventoryItems && (
            <div className="pt-4">
              <Loading />
            </div>
          )} */}

            <InventoryTable
              moverInventoryItems={moverInventoryItems || []}
              items={filteredItems as InventoryItemForm[]}
              category={selectedCategory}
              onCategoryChange={onCategoryChange}
              updateCustomItem={updateCustomItem}
              onQuantityChange={handleQuantityChange}
              onDeleteItem={deleteCustomItem}
              getItemQuantity={getItemQuantity}
              onAddCustomItem={addCustomItem}
              onSearchChange={onSearchChange}
            />
          </SheetContent>
        </Sheet>
      ) : (
        <div>
          <InventoryToolbar
            searchTerm={searchTerm}
            inventory={inventory}
            onSearchChange={onSearchChange}
            onSave={saveValues}
            showSummary={showSummary}
          />

          <InventoryTable
            moverInventoryItems={moverInventoryItems || []}
            items={filteredItems as InventoryItemForm[]}
            category={selectedCategory}
            onCategoryChange={onCategoryChange}
            updateCustomItem={updateCustomItem}
            onQuantityChange={handleQuantityChange}
            onDeleteItem={deleteCustomItem}
            getItemQuantity={getItemQuantity}
            onAddCustomItem={addCustomItem}
            onSearchChange={onSearchChange}
            customerFacing={customerFacing}
          />
        </div>
      )}
    </div>
  );
};

export default Inventory;
